<template>
  <div id="establishmentSection" class="columns-container container-spacing">
    <div class="column">
      <div class="change-order-page__header-left section-header">
        <h3>
          {{ i18n.t('/change-order/sections/dealer-and-location') }}
        </h3>
      </div>

      <app-fieldset :title="i18n.t('/change-order/location/locations')" class="establishment">
        <div class="establishment-id-group">
          <div class="change-owning-dealer-container">
            <label class="label">
              {{ i18n.t('/change-order/location/dealer-location') }}
            </label>
            <p>{{ originalEstablishmentName }}</p>
          </div>
          <div v-if="hasOriginalUnloadingLocation" class="change-shipping-location-container">
            <label class="label">
              {{ i18n.t('/change-order/location/location') }}
            </label>
            <p>{{ originalUnloadingLocationName }}</p>
          </div>
          <div v-if="dealerSettings.isImporter" class="transport-hold-input-panel">
            <div>
              <form-checkbox
                id="original-transport-hold-checkbox-checkbox"
                title="Uitgesteld transport"
              >
                <input
                  id="original-transport-hold-checkbox-checkbox"
                  type="checkbox"
                  name="original-transport-hold-checkbox"
                  disabled="disabled"
                  :checked="hasOriginalTransportHold"
                />
              </form-checkbox>
              <span>{{ originalTransportExpiryDate }}</span>
            </div>
          </div>
          <div v-if="canSeeAdditionalInfo" class="additionalInfo-input-container">
            <div class="additionalInfo-panel">
              <div>
                <label class="label">
                  {{ i18n.t('/change-order/location/additional-info') }}
                </label>
                <br />
                <span>{{ getOriginalAdditionalInfo }}</span>
              </div>
            </div>
          </div>
        </div>
      </app-fieldset>
    </div>

    <div class="column">
      <div class="change-order-page__header-right section-header">
        <h3>
          {{ i18n.t('/change-order/sections/dealer-and-location') }}
        </h3>
      </div>

      <div v-if="establishmentDataChanged" class="confirmation-message-container">
        <span class="confirmation-message">
          <i class="fas fa-arrows-rotate"></i>&nbsp;
          <strong>
            {{ i18n.t('/change-order/generic/modified') }}
          </strong>
          &nbsp; {{ i18n.t('/change-order/generic/modified-confirmation') }}
        </span>
      </div>

      <app-fieldset :title="i18n.t('/change-order/location/locations')" class="establishment">
        <div class="establishment-id-group">
          <div class="change-owning-dealer-container">
            <label class="label">
              {{ i18n.t('/change-order/location/dealer-location') }}
            </label>
            <div class="text-edit-container">
              <span class="editable-text">{{ changedEstablishmentName }}</span>
              <span
                v-if="canChangeEstablishment"
                class="edit-icon"
                @click="handleEstablishmentChanged"
              >
                <i class="fa-solid fa-pencil"></i>
              </span>
              <span
                v-if="hasEstablishmentChanged"
                class="edit-icon"
                @click="revertEstablishmentChanged"
              >
                <i class="fa-solid fa-trash-can"></i>
              </span>
              <span v-if="!canChangeEstablishment" class="edit-icon" :title="retrieveLockedReasons">
                <i class="fa-solid fa-lock"></i>
              </span>
            </div>
          </div>
          <div class="change-shipping-location-container">
            <label class="label">
              {{ i18n.t('/change-order/location/location') }}
            </label>
            <div class="text-edit-container">
              <span class="editable-text">{{ changedUnloadingLocationName }}</span>
              <span
                v-if="canChangeLoadingLocation"
                class="edit-icon"
                @click="handleLocationChanged"
              >
                <i class="fa-solid fa-pencil"></i>
              </span>
              <span
                v-if="hasChangedLoadingLocation"
                class="edit-icon"
                @click="revertLocationChanged"
              >
                <i class="fa-solid fa-trash-can"></i>
              </span>
              <span
                v-if="!canChangeLoadingLocation"
                class="edit-icon"
                :title="retrieveLockedReasons"
              >
                <i class="fa-solid fa-lock"></i>
              </span>
            </div>
            <div v-if="dealerSettings.isImporter" class="transport-hold-input-panel">
              <div class="transport-hold-column">
                <form-checkbox
                  id="changed-transport-hold-checkbox"
                  title="Uitgesteld transport"
                  :disabled="!canChangeTransportHold"
                >
                  <input
                    id="changed-transport-hold-checkbox"
                    v-model="tempChangedTransportHold"
                    name="changed-transport-hold-checkbox"
                    type="checkbox"
                    :disabled="!canChangeTransportHold"
                    @change="onTransportHoldChange"
                  />
                </form-checkbox>
              </div>
              <div
                v-if="tempChangedTransportHold"
                :disabled="!canChangeTransportHold || null"
                class="transportHoldEndDate-column"
              >
                <date-picker
                  id="transportHoldEndDate"
                  :date="transportHoldEndDate"
                  :allow-empty="true"
                  title=""
                  @change-date="changeTransportHoldEndDate"
                />
              </div>
            </div>
            <div v-if="canSeeAdditionalInfo" class="additionalInfo-input-container">
              <div v-if="canShowAddButton">
                <app-button
                  data-qa="enter-Add-Mode"
                  :title="retrieveLockedReasons"
                  @click="enterAddMode"
                >
                  <i :class="determineChangedClass"></i>
                  {{ i18n.t('/change-order/location/add-additional-info') }}
                </app-button>
              </div>
              <div v-else-if="showInput">
                <div class="additionalInfo-input-panel">
                  <label class="label" for="additionalInfo">
                    {{ i18n.t('/change-order/location/additional-info') }}
                  </label>
                  <br />
                  <input
                    id="additionalInfo"
                    v-model="additionalInfoInput"
                    type="text"
                    maxlength="30"
                  />
                </div>
                <div class="additionalInfo-button-panel">
                  <app-button data-qa="confirm-K-code" @click="confirmAdditionalInfoChange">
                    {{ i18n.t('/common/confirm') }}
                  </app-button>
                  <app-button data-qa="cancel-Add-Mode" @click="cancelAddMode">
                    {{ i18n.t('/common/cancel') }}
                  </app-button>
                </div>
              </div>
              <div v-else>
                <span v-if="hasChangedAdditionalInfo" class="confirmation-message">
                  <i class="fas fa-arrows-rotate"></i>&nbsp;
                  <strong>
                    {{ i18n.t('/change-order/generic/modified') }}
                  </strong>
                  &nbsp; {{ i18n.t('/change-order/generic/modified-confirmation') }}
                </span>
                <div class="additionalInfo-panel">
                  <div>
                    <label class="label">
                      {{ i18n.t('/change-order/location/additional-info') }}
                    </label>
                    <br />
                    <span :class="determineAdditionalInfoChangedClass">
                      {{ getChangedAdditionalInfo }}
                    </span>
                  </div>
                  <div class="additionalInfo-button-panel">
                    <app-button
                      data-qa="enter-edit-mode"
                      :title="retrieveLockedReasons"
                      @click="enterEditMode"
                    >
                      <i :class="determineChangedClass"></i>&nbsp;
                      {{ i18n.t('/edit button label') }}
                    </app-button>
                    <app-button
                      v-if="hasChangedAdditionalInfo"
                      data-qa="remove-AdditionalInfo"
                      :title="retrieveLockedReasons"
                      @click="removeAdditionalInfo"
                    >
                      <i :class="determineChangedClass"></i>&nbsp;
                      {{ i18n.t('/change-order/actions/delete') }}
                    </app-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </app-fieldset>
    </div>

    <single-feature-change-dialog
      v-if="showEstablishmentChangeDialog"
      :show-id="false"
      :show-prices="false"
      :show-options-search-input="true"
      :title="'Wijzig dealer & vestiging'"
      :items="availableEstablishments"
      @close="showEstablishmentChangeDialog = false"
      @confirm="confirmEstablishmentChange"
    />
    <single-feature-change-dialog
      v-if="showUnloadingLocationChangeDialog"
      :show-id="false"
      :show-prices="false"
      :show-options-search-input="true"
      :title="'Wijzig loslocatie'"
      max-width="40%"
      :items="availableUnloadingLocations"
      @close="showUnloadingLocationChangeDialog = false"
      @confirm="confirmUnloadingLocationsChange"
    />
    <error-modal :title="i18n.t('/change-order/location/error')" :errors="errors" />
  </div>
</template>

<script>
import AppFieldset from '@/components/generic/presentational/atoms/appFieldset/AppFieldset.vue'
import StoreConnector from '@/helpers/StoreConnector'
import { useVuelidate } from '@vuelidate/core'
import { mapGetters } from 'vuex'
import SingleFeatureChangeDialog from '@/components/changeOrder/presentational/organisms/singleFeatureChangeDialog/singleFeatureChangeDialog.vue'
import ErrorModal from '@/components/generic/presentational/atoms/errorModal/ErrorModal.vue'
import ChangeOrderStatus from '@/helpers/enums/changeOrderStatus'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import FormCheckbox from '@/components/generic/presentational/atoms/formCheckbox/FormCheckbox.vue'
import DatePicker from '@/components/generic/presentational/atoms/datePicker/DatePicker.vue'
import moment from 'moment/moment'

export default {
  components: {
    DatePicker,
    FormCheckbox,
    AppButton,
    ErrorModal,
    SingleFeatureChangeDialog,
    AppFieldset,
  },
  directives: {
    focus: {
      inserted(el) {
        const search = el.querySelector('input[type=search]')
        if (search) search.focus()
      },
    },
  },
  emits: ['change-unloading-location', 'change-establishment-id'],
  setup() {
    return { v$: useVuelidate() }
  },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      establishments: [],
      unloadingLocations: [],
      readonlyEstablishmentId: null,
      showError: false,
      showEstablishmentChangeDialog: false,
      showUnloadingLocationChangeDialog: false,
      errors: null,
      showAddButton: true,
      showInput: false,
      additionalInfoInput: null,
      transportHoldEndDate: null,
      tempChangedTransportHold: false,
    }
  },
  computed: {
    ...mapGetters({
      changeOrder: 'changeOrder/get',
      dealerSettings: 'user/dealerSettings',
    }),
    canChangeTransportHold() {
      return (
        this.changeOrder.Permissions?.CanChangeHoldTransport?.Allowed === true &&
        this.changeOrder.TransportHold?.OrderStatus !== ChangeOrderStatus.submitted &&
        this.changeOrder.MainOrderStatus !== ChangeOrderStatus.submitted &&
        this.changeOrder.MainOrderStatus !== ChangeOrderStatus.archived
      )
    },
    hasOriginalTransportHold() {
      return this.changeOrder.Holds.TransportHold.OriginalTransportHold === true
    },
    originalTransportExpiryDate() {
      if (this.hasOriginalTransportHold) {
        let originalTransportHoldExpiryDate =
          this.changeOrder.Holds.TransportHold.OriginalTransportHoldExpiryDate

        if (originalTransportHoldExpiryDate) {
          return ' t/m ' + originalTransportHoldExpiryDate
        }
      }

      return ''
    },
    canSeeAdditionalInfo() {
      return this.dealerSettings.isImporter
    },
    availableEstablishments() {
      return this.establishments.map((establishment) => {
        return {
          Id: establishment.Id,
          Name: `${establishment.Id} - ${establishment.Name} - ${establishment.City}`,
        }
      })
    },
    originalEstablishmentName() {
      if (this.establishments) {
        const establishment = this.establishments.find(
          (a) => a.Id === this.changeOrder.OwningDealer.OriginalPartnerNumber
        )

        if (establishment != null) return this.formatEstablishmentName(establishment)
      }
      return 'Onbekende dealer & vestiging'
    },
    changedEstablishmentName() {
      if (this.establishments) {
        const establishment = this.establishments.find(
          (a) => a.Id === this.changeOrder.OwningDealer.ChangedPartnerNumber
        )

        if (establishment != null) return this.formatEstablishmentName(establishment)
      }
      return 'Onbekende dealer & vestiging'
    },
    availableUnloadingLocations() {
      return this.unloadingLocations.map((location) => {
        return {
          Id: location.Id,
          DeliveredToDealerNumber: location.DeliveredToDealerNumber,
          Name: `${location.Name}, ${location.Address}, ${location.ZipCode}, ${location.City}`,
        }
      })
    },
    originalUnloadingLocationName() {
      if (this.unloadingLocations) {
        let originalUnloadingLocationId =
          this.changeOrder.UnloadingLocation.OriginalUnloadingLocationId

        const unloadingLocation = this.unloadingLocations.find(
          (a) => a.Id === originalUnloadingLocationId
        )

        if (unloadingLocation) {
          return this.formatUnloadingLocationName(unloadingLocation)
        }
      }

      return 'Onbekende loslocatie'
    },
    changedUnloadingLocationName() {
      if (this.unloadingLocations) {
        let changedUnloadingLocationId =
          this.changeOrder.UnloadingLocation.ChangedUnloadingLocationId

        if (changedUnloadingLocationId != null) {
          const unloadingLocation = this.unloadingLocations.find(
            (a) => a.Id === changedUnloadingLocationId
          )

          if (unloadingLocation) {
            return this.formatUnloadingLocationName(unloadingLocation)
          }
        } else {
          return 'Standaard loslocatie'
        }
      }

      return 'Onbekende loslocatie'
    },
    hasOriginalUnloadingLocation() {
      return !!this.changeOrder.UnloadingLocation.OriginalUnloadingLocationId
    },
    hasChangedUnloadingLocation() {
      return !!this.changeOrder.UnloadingLocation.ChangedUnloadingLocationId
    },
    hasEstablishmentChanged() {
      return (
        this.changeOrder.OwningDealer.ChangedPartnerNumber !==
        this.changeOrder.OwningDealer.OriginalPartnerNumber
      )
    },
    hasChangedLoadingLocation() {
      return (
        this.changeOrder.UnloadingLocation.ChangedUnloadingLocationId !==
        this.changeOrder.UnloadingLocation.OriginalUnloadingLocationId
      )
    },
    canChangeEstablishment() {
      return (
        this.dealerSettings.isImporter &&
        this.changeOrder.Permissions.CanChangeOwningDealer.Allowed === true &&
        this.changeOrder.Permissions.CanChangeUnloadingLocation.Allowed === true &&
        this.changeOrder.UnloadingLocation.OrderStatus !== ChangeOrderStatus.submitted &&
        this.changeOrder.MainOrderStatus !== ChangeOrderStatus.submitted &&
        this.changeOrder.MainOrderStatus !== ChangeOrderStatus.archived
      )
    },
    canChangeLoadingLocation() {
      return (
        this.changeOrder.Permissions.CanChangeOwningDealer.Allowed === true &&
        this.changeOrder.Permissions.CanChangeUnloadingLocation.Allowed === true &&
        this.changeOrder.UnloadingLocation.OrderStatus !== ChangeOrderStatus.submitted &&
        this.changeOrder.MainOrderStatus !== ChangeOrderStatus.submitted &&
        this.changeOrder.MainOrderStatus !== ChangeOrderStatus.archived
      )
    },
    canShowAddButton() {
      return this.showAddButton && this.changeOrder.AdditionalInfo.OrderStatus === null
    },
    canAdditionalInfoChange() {
      return (
        this.changeOrder.Permissions.CanChangeRemark.Allowed === true &&
        this.changeOrder.AdditionalInfo.OrderStatus !== ChangeOrderStatus.submitted &&
        this.changeOrder.MainOrderStatus !== ChangeOrderStatus.submitted &&
        this.changeOrder.MainOrderStatus !== ChangeOrderStatus.archived
      )
    },
    retrieveLockedReasons() {
      let lockedReasons = []
      if (!this.canAdditionalInfoChange) {
        if (!this.changeOrder.Permissions?.CanChangeRemark?.Allowed) {
          lockedReasons.push(this.changeOrder.Permissions?.CanChangeRemark?.Reason)
        }
        if (
          this.changeOrder.AdditionalInfo?.OrderStatus === ChangeOrderStatus.submitted ||
          this.changeOrder.MainOrderStatus === ChangeOrderStatus.submitted ||
          this.changeOrder.MainOrderStatus === ChangeOrderStatus.archived
        ) {
          lockedReasons.push('Order has been submitted or deleted')
        }
      }
      return lockedReasons.join('\n')
    },
    determineAdditionalInfoChangedClass() {
      if (this.hasChangedAdditionalInfo) {
        return 'additionalInfo-changed'
      }
      return ''
    },
    determineChangedClass() {
      if (this.canAdditionalInfoChange) {
        return 'fa-solid fa-pencil'
      } else {
        return 'fa-solid fa-lock'
      }
    },
    getOriginalAdditionalInfo() {
      let originalAdditionalInfo = this.changeOrder.AdditionalInfo.OriginalAdditionalInfo
      if (originalAdditionalInfo !== null) {
        return originalAdditionalInfo
      }
      return '-'
    },
    hasChangedAdditionalInfo() {
      return (
        this.changeOrder.AdditionalInfo.OriginalAdditionalInfo !==
        this.changeOrder.AdditionalInfo.ChangedAdditionalInfo
      )
    },
    hasChangedTransportHold() {
      return (
        this.changeOrder.Holds.TransportHold.OriginalTransportHold !==
          this.changeOrder.Holds.TransportHold.ChangedTransportHold ||
        this.changeOrder.Holds.TransportHold.OriginalTransportHoldExpiryDate !==
          this.changeOrder.Holds.TransportHold.ChangedTransportHoldExpiryDate
      )
    },
    establishmentDataChanged() {
      return (
        this.hasEstablishmentChanged ||
        this.hasChangedLoadingLocation ||
        this.hasChangedAdditionalInfo ||
        this.hasChangedTransportHold
      )
    },
    getChangedAdditionalInfo() {
      let changedAdditionalInfo = this.changeOrder.AdditionalInfo.ChangedAdditionalInfo
      if (changedAdditionalInfo !== null) {
        return changedAdditionalInfo
      }
      return '-'
    },
  },
  created() {
    StoreConnector.get('changeOrder/unloadingLocations', (data) => {
      this.unloadingLocations = data
    })
    StoreConnector.get('changeOrder/establishments', (data) => {
      this.establishments = data
    })

    this.hasAlternativeLocation = this.hasChangedUnloadingLocation

    StoreConnector.dispatch('changeOrder/getEstablishments')
    StoreConnector.dispatch('changeOrder/getUnloadingLocations')
  },
  mounted() {
    this.setHolds()
  },
  methods: {
    setHolds() {
      this.tempChangedTransportHold = this.changeOrder.Holds.TransportHold.ChangedTransportHold

      if (this.changeOrder.Holds.TransportHold.ChangedTransportHold === true) {
        let changedTransportHoldExpiryDate =
          this.changeOrder.Holds.TransportHold.ChangedTransportHoldExpiryDate
        if (changedTransportHoldExpiryDate) {
          this.transportHoldEndDate = moment.utc(changedTransportHoldExpiryDate, 'DD-MM-YYYY')
        } else {
          this.transportHoldEndDate = null
        }
      }
    },
    changeTransportHoldEndDate(value) {
      this.transportHoldEndDate = value
      this.updateTransportHold()
    },
    onTransportHoldChange() {
      this.updateTransportHold()
    },
    updateTransportHold() {
      // Update the change order with the current transport hold state
      this.changeOrder.Holds.TransportHold.ChangedTransportHold = this.tempChangedTransportHold

      if (this.tempChangedTransportHold && this.transportHoldEndDate) {
        this.changeOrder.Holds.TransportHold.ChangedTransportHoldExpiryDate =
          this.transportHoldEndDate.format('DD-MM-YYYY')
      } else {
        this.changeOrder.Holds.TransportHold.ChangedTransportHoldExpiryDate = null
      }

      // Commit the change to the store (assuming there's a mutation for this)
      this.$store.commit('changeOrder/setTransportHoldStatus')
    },
    formatEstablishmentName(establishment) {
      return `${establishment.Id} - ${establishment.Name} - ${establishment.City}`
    },
    formatUnloadingLocationName(unloadingLocation) {
      return `${unloadingLocation.Name}, ${unloadingLocation.Address}, ${unloadingLocation.ZipCode}, ${unloadingLocation.City}`
    },
    handleEstablishmentChanged() {
      this.showEstablishmentChangeDialog = true
    },
    revertEstablishmentChanged() {
      this.changeOrder.OwningDealer.ChangedPartnerNumber =
        this.changeOrder.OwningDealer.OriginalPartnerNumber
    },
    handleLocationChanged() {
      this.showUnloadingLocationChangeDialog = true
    },
    revertLocationChanged() {
      this.changeOrder.UnloadingLocation.ChangedUnloadingLocationId =
        this.changeOrder.UnloadingLocation.OriginalUnloadingLocationId
    },
    async confirmEstablishmentChange(selectedEstablishment) {
      this.changeEstablishmentId(selectedEstablishment)
      this.changeUnloadingLocationId(null)
    },
    async confirmUnloadingLocationsChange(selectedLocation) {
      this.changeUnloadingLocationId(selectedLocation)
    },
    changeEstablishmentId(selectedEstablishment) {
      this.changeOrder.OwningDealer.ChangedPartnerNumber = selectedEstablishment.Id
      this.$store.commit('changeOrder/setOwningDealerStatus')
    },
    changeUnloadingLocationId(unloadingLocation) {
      if (unloadingLocation !== null) {
        this.changeOrder.UnloadingLocation.ChangedUnloadingLocationId = unloadingLocation.Id
        this.changeOrder.UnloadingLocation.ChangedDeliveredToDealerNumber =
          unloadingLocation.DeliveredToDealerNumber
      } else {
        this.changeOrder.UnloadingLocation.ChangedUnloadingLocationId = null
        this.changeOrder.UnloadingLocation.ChangedDeliveredToDealerNumber = null
      }
      this.$store.commit('changeOrder/setUnloadingLocationStatus')
    },
    enterAddMode() {
      this.showAddButton = false
      this.showInput = true
    },
    cancelAddMode() {
      this.showAddButton = true
      this.showInput = false
      this.additionalInfoInput = ''
    },
    confirmAdditionalInfoChange() {
      this.changeOrder.AdditionalInfo.ChangedAdditionalInfo = this.additionalInfoInput
      this.showInput = false
      this.$store.commit('changeOrder/setAdditionalInfoStatus')
    },
    enterEditMode() {
      if (this.canAdditionalInfoChange) {
        this.additionalInfoInput = this.changeOrder.AdditionalInfo.ChangedAdditionalInfo
        this.enterAddMode()
      }
    },
    removeAdditionalInfo() {
      if (this.canAdditionalInfoChange) {
        let originalAdditionalInfo = this.changeOrder.AdditionalInfo.OriginalAdditionalInfo

        this.additionalInfoInput = originalAdditionalInfo
        this.changeOrder.AdditionalInfo.ChangedAdditionalInfo = originalAdditionalInfo

        this.showAddButton = true
        this.$store.commit('changeOrder/setAdditionalInfoStatus')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.columns-container {
  display: flex;
  justify-content: space-between;
}

.column {
  flex: 1;
  margin: 0 20px;
}

:deep(.establishment) {
  .fields {
    margin-bottom: 0;
  }
}

.label {
  font-weight: 700;
}

.container-spacing {
  margin-top: 50px;
}

h2 {
  font-size: 1.46191rem;
  margin-bottom: var(--space-xxs);
}

h3 {
  font-weight: 400;
  margin: 0;
}

.change-order {
  &-page__header-left {
    h3 {
      color: $black;
    }
  }

  &-page__header-right {
    h3 {
      color: $color-primary;
    }
  }
}

.section-header {
  border-bottom: 1px solid #a3b8c0;
  align-items: center;
  margin-bottom: 1rem;
}

.editable-container {
  display: flex;
  align-items: center;
}

.edit-icon {
  margin-left: 10px;
  cursor: pointer;
}

.change-owning-dealer-container {
  margin-bottom: 30px;
}

.confirmation-message {
  background-color: #ffdd9c;
  font-size: 18px;
  padding: 10px;
}

.additionalInfo-changed {
  background-color: #fff8b9;
}

.additionalInfo-input-container {
  margin-top: 40px;
}

.additionalInfo-input-panel {
  label {
    font-weight: bold;
    margin-bottom: 3px;
  }
  input {
    width: 400px;
    border: 1px solid black;
  }
}

.transport-hold-input-panel {
  margin-top: 25px;
  height: 50px;
}

.additionalInfo-panel {
  margin-top: 20px;
  label {
    font-weight: bold;
    margin-bottom: 2px;
  }
  span {
    padding: 5px;
  }
}

.additionalInfo-button-panel {
  margin-top: 20px;
}

.transport-hold-input-panel {
  display: flex;
  flex-direction: row;
}

.transportHoldEndDate-column {
  margin-left: 35px;
  padding: 5px;
}

.confirmation-message {
  margin-top: 5px;
  background-color: #ffdd9c;
  font-size: 18px;
  padding: 10px;
}

.confirmation-message-container {
  margin-bottom: 15px;
}
</style>
